import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  Tooltip,
  Modal,
  TextField,
  Autocomplete,
  IconButton,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import axios from 'axios';
import LoadingSpinner from '../../_GlobalComponents/LoadingSpinner';
import RowDetailModal from './SuggestedOrderTable/Components/RowDetailModal';
import { AddShoppingCart } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import OrderConfirmModal from './OrderConfirmModal/OrderConfirmModal';
import AddItemPopup from './AddItemPopup';
import {
  addToSmartOrderCart,
  deleteFromSmartOrderCart,
  updateCartItemQuantity,
} from '../../../Redux/SmartOrderCartSlice';
import { green } from '@mui/material/colors';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { resolveCustomerToken } from '../utils/customerToken';

const TOP_FACET_KEYS = ['top_facet_1', 'top_facet_2', 'top_facet_3', 'top_facet_4', 'top_facet_5'];
const ALPHANUMERIC_SORTER = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const isNumericLeadingValue = (value) => /^\d/.test(String(value).trim());

const compareFacetValues = (a, b) => {
  const aNumericFirst = isNumericLeadingValue(a);
  const bNumericFirst = isNumericLeadingValue(b);

  if (aNumericFirst && !bNumericFirst) return -1;
  if (!aNumericFirst && bNumericFirst) return 1;

  return ALPHANUMERIC_SORTER.compare(String(a), String(b));
};

const ResponsiveFacetFilters = ({
  manufacturers,
  selectedManufacturer,
  setSelectedManufacturer,
  types,
  selectedType,
  setSelectedType,
  categories,
  selectedCategory,
  setSelectedCategory,
  facetNames,
  facetOptionsByName,
  selectedFacetFilters,
  setFacetFilter,
  onClearAllFilters,
  toolbarFieldSx,
}) => {
  const rootRef = React.useRef(null);
  const fixedControlsRef = React.useRef(null);
  const [maxVisibleFacetCount, setMaxVisibleFacetCount] = useState(0);

  const CONTROL_GAP_PX = 6;
  const FACET_CONTROL_WIDTH_PX = 200;
  const CLEAR_BUTTON_WIDTH_PX = 80;
  const hasActiveFilters =
    selectedManufacturer !== 'All' ||
    selectedType !== 'All' ||
    selectedCategory !== 'All' ||
    Object.values(selectedFacetFilters).some(Boolean);

  useEffect(() => {
    const rootElement = rootRef.current;
    const fixedControlsElement = fixedControlsRef.current;

    if (!rootElement || !fixedControlsElement) {
      return undefined;
    }

    const recalculateVisibleFacetCount = () => {
      const rootWidth = rootElement.getBoundingClientRect().width;
      const fixedWidth = fixedControlsElement.getBoundingClientRect().width;
      const clearButtonWidth = hasActiveFilters ? CLEAR_BUTTON_WIDTH_PX : 0;
      const availableWidth = Math.max(
        0,
        rootWidth - fixedWidth - clearButtonWidth - CONTROL_GAP_PX * 2,
      );
      const slotWidth = FACET_CONTROL_WIDTH_PX + CONTROL_GAP_PX;
      const computedCount = Math.max(0, Math.floor(availableWidth / slotWidth));
      const hasFacetFilters = selectedCategory !== 'All' && selectedCategory !== '' && facetNames.length > 0;
      const nextCount = hasFacetFilters ? Math.max(1, computedCount) : computedCount;
      setMaxVisibleFacetCount(nextCount);
    };

    recalculateVisibleFacetCount();

    const resizeObserver = new ResizeObserver(() => {
      recalculateVisibleFacetCount();
    });

    resizeObserver.observe(rootElement);
    resizeObserver.observe(fixedControlsElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [facetNames.length, hasActiveFilters, selectedCategory]);

  const orderedFacetNames = useMemo(() => {
    const selectedNames = facetNames.filter((name) => Boolean(selectedFacetFilters[name]));
    const unselectedNames = facetNames.filter((name) => !selectedFacetFilters[name]);
    return [...selectedNames, ...unselectedNames];
  }, [facetNames, selectedFacetFilters]);

  const visibleFacetNames = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '') {
      return [];
    }
    return orderedFacetNames.slice(0, maxVisibleFacetCount);
  }, [maxVisibleFacetCount, orderedFacetNames, selectedCategory]);

  return (
    <Box
      ref={rootRef}
      display='flex'
      gap={0.75}
      alignItems='flex-end'
      flexWrap='nowrap'
      sx={{ width: '100%', overflow: 'hidden' }}
    >
      <Box
        ref={fixedControlsRef}
        display='flex'
        gap={0.75}
        alignItems='flex-end'
        flexShrink={0}
      >
        <Autocomplete
          sx={toolbarFieldSx}
          options={manufacturers}
          value={selectedManufacturer === 'All' ? null : selectedManufacturer}
          onChange={(_, newValue) => setSelectedManufacturer(!newValue ? 'All' : newValue)}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Manufacturer' />}
          clearOnBlur={false}
        />
        <Autocomplete
          sx={toolbarFieldSx}
          options={types}
          value={selectedType === 'All' ? null : selectedType}
          onChange={(_, newValue) => setSelectedType(!newValue ? 'All' : newValue)}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Item Type' />}
          clearOnBlur={false}
        />
        <Autocomplete
          sx={toolbarFieldSx}
          options={categories}
          value={selectedCategory === 'All' ? null : selectedCategory}
          onChange={(_, newValue) => setSelectedCategory(!newValue ? 'All' : newValue)}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Category' />}
          clearOnBlur={false}
        />
      </Box>

      <Box display='flex' gap={0.75} alignItems='flex-end' sx={{ minWidth: 0, overflow: 'hidden' }}>
        {visibleFacetNames.map((facetName) => (
          <Autocomplete
            key={facetName}
            sx={toolbarFieldSx}
            options={facetOptionsByName[facetName] || []}
            value={selectedFacetFilters[facetName] || null}
            onChange={(_, newValue) => setFacetFilter(facetName, newValue || '')}
            renderInput={(params) => <TextField {...params} size='small' placeholder={facetName} />}
            clearOnBlur={false}
          />
        ))}
      </Box>

      {hasActiveFilters && (
        <Button
          size='small'
          aria-label='Clear all filters'
          onClick={onClearAllFilters}
          sx={{
            flexShrink: 0,
            minWidth: 'auto',
            px: 0.5,
            color: 'common.white',
            textTransform: 'none',
            fontSize: '0.72rem',
            lineHeight: 1.05,
            textDecoration: 'underline',
            whiteSpace: 'pre-line',
            textAlign: 'center',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          {'Clear All\nFilters'}
        </Button>
      )}
    </Box>
  );
};

const SuggestionsView = ({
  onOpenHelp,
  onPersistAddItem,
  onPersistEditItem,
  onPersistRemoveItem,
  webOrderId,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [token, setToken] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageColor, setMessageColor] = useState('#000');
  const [pageTitle, setPageTitle] = useState('');
  const [userPersonalization, setUserPersonalization] = useState(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedFacetFilters, setSelectedFacetFilters] = useState({});
  const [qtyDrafts, setQtyDrafts] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItemNumber, setSelectedItemNumber] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [sortModel, setSortModel] = useState([{ field: 'score', sort: 'desc' }]);
  const [openAddItemModal, setOpenAddItemModal] = useState(false);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [isQuickSearchOpen, setIsQuickSearchOpen] = useState(false);
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const quickSearchRef = useRef(null);
  const hasQuickFilterValueRef = useRef(false);

  const selectedRows = useSelector((state) => state.smartOrderCart.value);

  useEffect(() => {
    hasQuickFilterValueRef.current = Boolean(filterModel.quickFilterValues?.length);
  }, [filterModel.quickFilterValues]);

  const flattenData = useCallback((details) => {
    const arr = [];
    Object.keys(details).forEach((categoryGroupKey) => {
      const items = details[categoryGroupKey];
      items.forEach((item) => {
        arr.push({
          ...item,
          category: item.category || 'Uncategorized',
        });
      });
    });
    return arr;
  }, []);

  useEffect(() => {
    const resolvedToken = resolveCustomerToken();
    setToken(resolvedToken);
    setIsLoading(true);

    axios
      .post('/suggestedOrders/getSmartBuys', { customerNumber: resolvedToken })
      .then((response) => {
        const { result, message, message_color, page_title, details, userId } = response.data;
        setMessage(message);
        setMessageColor(message_color || '#000');
        setPageTitle(page_title);
        if (result === 'success' && details) {
          setSuggestions(flattenData(details));
        }

        if (userId) {
          axios
            .post('/userPersonalization/getbyroute', {
              domain: 'TSW',
              route: 'suggested-orders',
              userId,
            })
            .then((userResponse) => {
              setUserPersonalization(userResponse.data);
            })
            .catch((error) => {
              console.error('Error fetching user personalization:', error);
            });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
        setIsLoading(false);
      });
  }, [flattenData]);

  const normalizeFacetEntry = useCallback((entry) => {
    if (!entry) return null;
    if (typeof entry === 'object') {
      const [facetName, facetValue] = Object.entries(entry)[0] || [];
      if (!facetName) return null;
      const valueText =
        facetValue === null || facetValue === undefined ? '' : String(facetValue).trim();
      return {
        name: String(facetName).trim(),
        value: valueText,
      };
    }
    return null;
  }, []);

  const getRowFacetPairs = useCallback(
    (row) =>
      TOP_FACET_KEYS
        .map((key) => normalizeFacetEntry(row?.[key]))
        .filter((value) => Boolean(value?.name)),
    [normalizeFacetEntry],
  );

  const getRowFacetMap = useCallback(
    (row) =>
      getRowFacetPairs(row).reduce((acc, facet) => {
        acc[facet.name] = facet.value;
        return acc;
      }, {}),
    [getRowFacetPairs],
  );

  const manufacturers = useMemo(() => {
    const filtered =
      selectedType === 'All' || selectedType === ''
        ? suggestions
        : suggestions.filter((item) => item.item_type === selectedType);
    return Array.from(new Set(filtered.map((item) => item.manufacturer_name).filter(Boolean))).sort();
  }, [selectedType, suggestions]);

  const types = useMemo(() => {
    const filtered =
      selectedManufacturer === 'All' || selectedManufacturer === ''
        ? suggestions
        : suggestions.filter((item) => item.manufacturer_name === selectedManufacturer);
    return Array.from(new Set(filtered.map((item) => item.item_type).filter(Boolean))).sort();
  }, [selectedManufacturer, suggestions]);

  const categories = useMemo(() => {
    const filtered = suggestions.filter((item) => {
      const manufacturerMatches =
        selectedManufacturer === 'All' ||
        selectedManufacturer === '' ||
        item.manufacturer_name === selectedManufacturer;
      const typeMatches =
        selectedType === 'All' || selectedType === '' || item.item_type === selectedType;
      return manufacturerMatches && typeMatches;
    });
    return Array.from(new Set(filtered.map((item) => item.category).filter(Boolean))).sort();
  }, [selectedManufacturer, selectedType, suggestions]);

  useEffect(() => {
    if (!manufacturers.length) return;
    if (selectedManufacturer !== 'All' && !manufacturers.includes(selectedManufacturer)) {
      setSelectedManufacturer('All');
    }
  }, [manufacturers, selectedManufacturer]);

  useEffect(() => {
    if (!types.length) return;
    if (selectedType !== 'All' && !types.includes(selectedType)) {
      setSelectedType('All');
    }
  }, [selectedType, types]);

  useEffect(() => {
    if (!categories.length) return;
    if (selectedCategory !== 'All' && !categories.includes(selectedCategory)) {
      setSelectedCategory('All');
    }
  }, [categories, selectedCategory]);

  const baseFilteredRows = useMemo(() => {
    return suggestions.filter((item) => {
      const manufacturerMatches =
        selectedManufacturer === 'All' ||
        selectedManufacturer === '' ||
        item.manufacturer_name === selectedManufacturer;
      const typeMatches =
        selectedType === 'All' || selectedType === '' || item.item_type === selectedType;
      const categoryMatches =
        selectedCategory === 'All' || selectedCategory === '' || item.category === selectedCategory;
      return manufacturerMatches && typeMatches && categoryMatches;
    });
  }, [selectedCategory, selectedManufacturer, selectedType, suggestions]);

  const facetNames = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '') {
      return [];
    }

    const seen = new Set();
    const orderedNames = [];
    const categoryItems = suggestions.filter((item) => item.category === selectedCategory);

    categoryItems.forEach((item) => {
      TOP_FACET_KEYS.forEach((key) => {
        const facet = normalizeFacetEntry(item?.[key]);
        if (facet?.name && !seen.has(facet.name)) {
          seen.add(facet.name);
          orderedNames.push(facet.name);
        }
      });
    });

    return orderedNames;
  }, [normalizeFacetEntry, selectedCategory, suggestions]);

  const facetOptionsByName = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      return {};
    }

    return facetNames.reduce((acc, facetName) => {
      const rowsMatchingOtherFacetSelections = baseFilteredRows.filter((row) => {
        const facetMap = getRowFacetMap(row);
        return facetNames.every((name) => {
          if (name === facetName) return true;
          const selectedValue = selectedFacetFilters[name];
          if (!selectedValue) return true;
          return facetMap[name] === selectedValue;
        });
      });

      const options = Array.from(
        new Set(
          rowsMatchingOtherFacetSelections
            .map((row) => getRowFacetMap(row)[facetName])
            .filter(Boolean),
        ),
      ).sort(compareFacetValues);

      acc[facetName] = options;
      return acc;
    }, {});
  }, [baseFilteredRows, facetNames, getRowFacetMap, selectedCategory, selectedFacetFilters]);

  useEffect(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      setSelectedFacetFilters({});
      return;
    }

    setSelectedFacetFilters((prev) => {
      const next = {};
      facetNames.forEach((name) => {
        if (prev[name]) {
          next[name] = prev[name];
        }
      });
      return next;
    });
  }, [facetNames, selectedCategory]);

  useEffect(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      return;
    }

    setSelectedFacetFilters((prev) => {
      let changed = false;
      const next = { ...prev };

      facetNames.forEach((name) => {
        const selectedValue = next[name];
        if (!selectedValue) return;
        const options = facetOptionsByName[name] || [];
        if (!options.includes(selectedValue)) {
          delete next[name];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [facetNames, facetOptionsByName, selectedCategory]);

  const setFacetFilter = useCallback((facetName, value) => {
    setSelectedFacetFilters((prev) => {
      if (!value) {
        if (!prev[facetName]) return prev;
        const next = { ...prev };
        delete next[facetName];
        return next;
      }
      if (prev[facetName] === value) return prev;
      return {
        ...prev,
        [facetName]: value,
      };
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setSelectedManufacturer('All');
    setSelectedType('All');
    setSelectedCategory('All');
    setSelectedFacetFilters({});
    setFilterModel({ items: [], quickFilterValues: [] });
  }, []);

  const filteredSuggestions = useMemo(() => {
    return baseFilteredRows.filter((item) => {
      const rowFacetMap = getRowFacetMap(item);
      const facetMatches = facetNames.every((name) => {
        const selectedValue = selectedFacetFilters[name];
        if (!selectedValue) return true;
        return rowFacetMap[name] === selectedValue;
      });
      return facetMatches;
    });
  }, [baseFilteredRows, facetNames, getRowFacetMap, selectedFacetFilters]);

  const gridRows = useMemo(
    () =>
      filteredSuggestions.map((item, index) => ({
        ...item,
        _gridRowId: [
          item.item_number ?? 'missing-item-number',
          item.category ?? 'missing-category',
          item.manufacturer_name ?? 'missing-manufacturer',
          index,
        ].join('-'),
      })),
    [filteredSuggestions],
  );

  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [
    selectedManufacturer,
    selectedType,
    selectedCategory,
    selectedFacetFilters,
    filterModel.quickFilterValues,
  ]);

  const handleItemOpenModal = useCallback((itemNumber) => {
    setSelectedItemNumber(itemNumber);
    setModalOpen(true);
  }, []);

  const handleItemCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedItemNumber(null);
  }, []);

  const handleQtyChange = useCallback(
    (row, rawNewQty) => {
      const newQuantity = parseInt(rawNewQty, 10);

      if (isNaN(newQuantity) || newQuantity < 1) {
        return;
      }

      setSuggestions((prev) =>
        prev.map((item) =>
          item.item_number === row.item_number ? { ...item, suggested_qty: newQuantity } : item,
        ),
      );

      const isInCart = selectedRows.some((cartItem) => cartItem.item_number === row.item_number);
      if (isInCart) {
        dispatch(updateCartItemQuantity({ item_number: row.item_number, newQuantity }));
        onPersistEditItem?.(row.item_number, newQuantity);
      }
    },
    [dispatch, onPersistEditItem, selectedRows],
  );

  const handleStepperChange = useCallback(
    (row, delta) => {
      setQtyDrafts((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, row.item_number)) {
          return prev;
        }
        const next = { ...prev };
        delete next[row.item_number];
        return next;
      });

      const draftQty = qtyDrafts[row.item_number];
      const parsedDraft = parseInt(draftQty ?? '', 10);
      const cartItem = selectedRows.find((item) => item.item_number === row.item_number);
      const currentQty = !Number.isNaN(parsedDraft)
        ? parsedDraft
        : cartItem
          ? Number(cartItem.suggested_qty)
          : Number(row.suggested_qty || 1);
      const nextQty = currentQty + delta;
      handleQtyChange(row, String(nextQty));
    },
    [handleQtyChange, qtyDrafts, selectedRows],
  );

  const handleAddOrRemoveCart = useCallback(
    (row, shouldAdd) => {
      if (!shouldAdd) {
        dispatch(deleteFromSmartOrderCart(row.item_number));
        onPersistRemoveItem?.(row.item_number);
        return;
      }

      const cartItem = selectedRows.find((item) => item.item_number === row.item_number);
      if (cartItem) {
        return;
      }

      const cartPayload = {
        ...row,
        suggested_qty: (() => {
          const draftValue = qtyDrafts[row.item_number];
          const parsedDraft = parseInt(draftValue ?? '', 10);
          if (!Number.isNaN(parsedDraft) && parsedDraft > 0) {
            return parsedDraft;
          }
          return Number(row.suggested_qty || 1);
        })(),
        line_source: 'smart_buy',
      };

      dispatch(
        addToSmartOrderCart({
          ...cartPayload,
        }),
      );

      onPersistAddItem?.(cartPayload);
    },
    [dispatch, onPersistAddItem, onPersistRemoveItem, qtyDrafts, selectedRows],
  );

  const closeOrderModal = useCallback(() => {
    setIsOrderModalOpen(false);
  }, []);

  const handleConfirmOrder = useCallback(
    (poNumber, shippingMethod) => {
      const items = selectedRows.map((row) => ({
        item_number: row.item_number,
        suggested_qty: row.suggested_qty,
      }));
      axios
        .post('/suggestedOrders/placeOrder', {
          customerNumber: token,
          poNumber,
          shippingMethod,
          items,
          webOrderId,
        })
        .then(() => {
          console.log('Order placed successfully');
          closeOrderModal();
        })
        .catch((error) => {
          console.error('Error placing order:', error);
        });
    },
    [closeOrderModal, selectedRows, token, webOrderId]
  );

  const getPersonalizedValue = useCallback(
    (key) => {
      const match = userPersonalization?.find((p) => p.personalized_element === key);
      return match?.personalized_value;
    },
    [userPersonalization],
  );

  const yourQtyHeader = useMemo(() => getPersonalizedValue('YourQtyHeader') || 'Your Qty', [
    getPersonalizedValue,
  ]);

  const renderHelpHeader = useCallback(
    (label, columnName) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.35,
          minWidth: 0,
          pr: 2.5,
        }}
      >
        <Tooltip title='What does this mean?'>
          <IconButton
            size='small'
            aria-label={`What does ${label} mean?`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onOpenHelp?.({
                faqDescription: 'What do all the columns mean',
                sectionTitle: 'Smart Buys Tab',
                columnName,
              });
            }}
            sx={{ p: 0.25 }}
          >
            <HelpOutlineIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Typography variant='body2' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      </Box>
    ),
    [onOpenHelp],
  );

  const columns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 210,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row;
          const cartItem = selectedRows.find((item) => item.item_number === row.item_number);
          const isInCart = Boolean(cartItem);
          const currentQty = cartItem ? Number(cartItem.suggested_qty) : Number(row.suggested_qty || 1);
          const draftQty = qtyDrafts[row.item_number];
          const displayQty = draftQty !== undefined ? draftQty : String(currentQty);

          return (
            <Stack spacing={0.4} sx={{ width: 'auto', py: 0.25 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: 'lightgrey',
                  borderRadius: 1.5,
                  p: 0.8,
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderWidth: 0,
                    backgroundColor: 'rgba(255,255,255,.6)',
                    borderRadius: 0.75,
                    px: 0.5,
                    width: 92,
                    height: 30,
                    flex: '0 0 auto',
                  }}
                >
                  <IconButton
                    size='small'
                    sx={{
                      p: 0.25,
                      color: '#fff',
                      backgroundColor: 'rgba(000,000,000,0.2)',
                      borderRadius: 0.75,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.35)' },
                    }}
                    onClick={() => handleStepperChange(row, -1)}
                  >
                    <RemoveIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Box
                    component='input'
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    value={displayQty}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      if (!/^\d*$/.test(rawValue)) {
                        return;
                      }

                      setQtyDrafts((prev) => ({
                        ...prev,
                        [row.item_number]: rawValue,
                      }));

                      if (rawValue !== '') {
                        handleQtyChange(row, rawValue);
                      }
                    }}
                    onBlur={() => {
                      setQtyDrafts((prev) => {
                        if (!Object.prototype.hasOwnProperty.call(prev, row.item_number)) {
                          return prev;
                        }

                        const rawValue = prev[row.item_number];
                        const next = { ...prev };

                        if (rawValue === '') {
                          delete next[row.item_number];
                          return next;
                        }

                        const parsedValue = parseInt(rawValue, 10);
                        if (Number.isNaN(parsedValue)) {
                          delete next[row.item_number];
                          return next;
                        }

                        const normalizedValue = String(parsedValue);
                        if (normalizedValue !== rawValue) {
                          next[row.item_number] = normalizedValue;
                        } else {
                          delete next[row.item_number];
                        }

                        return next;
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      fontWeight: 700,
                      minWidth: 24,
                      width: 24,
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      color: theme.palette.primary.main,
                      cursor: 'text',
                      background: 'transparent',
                      border: 0,
                      outline: 0,
                      p: 0,
                      MozAppearance: 'textfield',
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                  />
                  <IconButton
                    size='small'
                    sx={{
                      p: 0.25,
                      color: '#fff',
                      backgroundColor: 'rgba(000,000,000,0.2)',
                      borderRadius: 0.75,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.35)' },
                    }}
                    onClick={() => handleStepperChange(row, 1)}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <Button
                  size='small'
                  variant='contained'
                  color='inherit'
                  onClick={() => handleAddOrRemoveCart(row, !isInCart)}
                  sx={{
                    minWidth: 30,
                    width: 30,
                    height: 30,
                    p: 0,
                    color: '#fff',
                    backgroundColor: isInCart ? '#ff6b6b' : theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: isInCart ? '#e25555' : theme.palette.primary.dark,
                    },
                  }}
                  aria-label={isInCart ? 'Remove from cart' : 'Add to cart'}
                >
                  {isInCart ? (
                    <DeleteOutlineIcon sx={{ fontSize: 16, color: 'white' }} />
                  ) : (
                    <AddShoppingCart sx={{ fontSize: 16 }} />
                  )}
                </Button>
              </Box>

              <Box
                onClick={() => {
                  setSelectedItemForAdd(row.item_number);
                  setOpenAddItemModal(true);
                }}
                sx={{
                  cursor: 'pointer',
                  color: '#1976d2',
                  textDecoration: 'underline',
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                Add Item to Min/Max List
              </Box>
            </Stack>
          );
        },
      },
      {
        field: 'dealer_mtd_sold',
        headerName: 'Sales Velocity',
        renderHeader: () => renderHelpHeader('Sales Velocity', 'Sales Velocity'),
        width: 130,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant='body1'>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: 'dealer_quantity_on_hand',
        headerName: yourQtyHeader,
        renderHeader: () => renderHelpHeader(yourQtyHeader, 'Your Qty'),
        width: 120,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant='body1'>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: 'quantity_on_hand',
        headerName: 'SS Qty',
        width: 110,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant='body1'>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: 'category',
        headerName: 'Category',
        renderHeader: () => renderHelpHeader('Category', 'Category'),
        width: 150,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant='body1'>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        renderHeader: () => renderHelpHeader('Description', 'Description'),
        minWidth: 360,
        flex: 1,
        renderCell: (params) => {
          const itemNumber = params.row.item_number;
          const upc = params.row.upc;
          const itemLabel =
            itemNumber !== undefined && itemNumber !== null && String(itemNumber).trim() !== ''
              ? itemNumber
              : 'N/A';
          const upcLabel = upc !== undefined && upc !== null && String(upc).trim() !== '' ? upc : 'N/A';

          return (
            <Box
              onClick={() => handleItemOpenModal(itemNumber)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                cursor: 'pointer',
              }}
            >
              <Typography variant='body1' sx={{ color: '#1976d2', textDecoration: 'underline' }}>
                {params.row.description}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {`SS Item #: ${itemLabel}`}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {`UPC #: ${upcLabel}`}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'cost',
        headerName: 'Cost',
        renderHeader: () => renderHelpHeader('Cost', 'Cost'),
        width: 120,
        renderCell: (params) => {
          const onSale = params.row.on_sale;
          let textColor = 'inherit';
          if (onSale) {
            textColor = green[700];
          }
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                borderRadius: 1,
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography
                variant='body1'
                sx={{ fontWeight: onSale ? 'bold' : 'normal', color: textColor }}
              >
                ${params.value}
              </Typography>
              {onSale && (
                <Tooltip title='On Sale!' placement='right'>
                  <LocalOfferIcon sx={{ ml: 1, color: green[700] }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      { field: 'score', headerName: 'Score', width: 120 },
      { field: 'item_number', headerName: 'SS Item #', width: 1, sortable: false },
    ],
    [
      handleAddOrRemoveCart,
      handleQtyChange,
      handleItemOpenModal,
      handleStepperChange,
      qtyDrafts,
      renderHelpHeader,
      selectedRows,
      theme.palette.primary.dark,
      theme.palette.primary.main,
      yourQtyHeader,
    ]
  );

  const toolbarFieldSx = useMemo(
    () => ({
      minWidth: 200,
      '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.common.white,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey[400],
      },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey[500],
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    }),
    [theme],
  );

  const SuggestionsToolbar = useCallback(
    () => (
      <GridToolbarContainer
        sx={{
          px: 0.75,
          py: 0.75,
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 0.75,
          backgroundColor: theme.palette.primary.main,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <ResponsiveFacetFilters
            manufacturers={manufacturers}
            selectedManufacturer={selectedManufacturer}
            setSelectedManufacturer={setSelectedManufacturer}
            types={types}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            facetNames={facetNames}
            facetOptionsByName={facetOptionsByName}
            selectedFacetFilters={selectedFacetFilters}
            setFacetFilter={setFacetFilter}
            onClearAllFilters={handleClearAllFilters}
            toolbarFieldSx={toolbarFieldSx}
          />
        </Box>

        <Box sx={{ position: 'relative', width: 260, height: 34, ml: 'auto' }}>
          <Box
            sx={{
              position: 'absolute',
              right: 36,
              top: 0,
              width: 220,
              transformOrigin: 'right center',
              transform: isQuickSearchOpen
                ? 'translateX(0) scaleX(1)'
                : 'translateX(10px) scaleX(0.85)',
              opacity: isQuickSearchOpen ? 1 : 0,
              pointerEvents: isQuickSearchOpen ? 'auto' : 'none',
              transition: 'transform 220ms ease, opacity 220ms ease',
            }}
          >
            <GridToolbarQuickFilter
              inputRef={quickSearchRef}
              debounceMs={300}
              placeholder='Search'
              onBlur={() => {
                if (!hasQuickFilterValueRef.current) {
                  setIsQuickSearchOpen(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && !hasQuickFilterValueRef.current) {
                  setIsQuickSearchOpen(false);
                }
              }}
              InputProps={{ startAdornment: null }}
              sx={{
                width: 220,
                pb: 0,
                '& input': {
                  color: theme.palette.common.white,
                },
                '& input::placeholder': {
                  color: theme.palette.common.white,
                  opacity: 0.9,
                },
                '& .MuiInput-underline:before': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiInput-underline:hover:before': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.common.white,
                },
              }}
            />
          </Box>
          <IconButton
            aria-label='search'
            onClick={() => {
              if (!isQuickSearchOpen) {
                setIsQuickSearchOpen(true);
                setTimeout(() => {
                  quickSearchRef.current?.focus();
                }, 0);
                return;
              }
              if (!hasQuickFilterValueRef.current) {
                setIsQuickSearchOpen(false);
              }
            }}
            sx={{ position: 'absolute', right: 0, top: 0, color: theme.palette.common.white }}
          >
            <SearchIcon fontSize='small' />
          </IconButton>
        </Box>
      </GridToolbarContainer>
    ),
    [
      categories,
      facetNames,
      facetOptionsByName,
      isQuickSearchOpen,
      manufacturers,
      selectedCategory,
      selectedFacetFilters,
      selectedManufacturer,
      selectedType,
      setFacetFilter,
      handleClearAllFilters,
      toolbarFieldSx,
      types,
      theme.palette.common.white,
      theme.palette.primary.main,
    ],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
        minHeight: 0,
        flexGrow: 1,
        p: 2,
        overflow: 'hidden',
      }}
    >
      <LoadingSpinner isLoading={isLoading} />

      <Box display='flex' flexDirection='column' gap={1} sx={{ mb: 2 }}>
        <Box display='flex' justifyContent='center' alignItems='baseline' flexWrap='wrap' gap={1}>
          <Typography variant='body1' sx={{ color: messageColor, fontSize: '1.1rem' }}>
            {message}
          </Typography>
          {pageTitle && (
            <Typography variant='h6' sx={{ fontWeight: 600 }}>
              {pageTitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          getRowId={(row) => row._gridRowId}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 88}
          disableRowSelectionOnClick
          disableColumnMenu
          showToolbar
          slots={{ toolbar: SuggestionsToolbar }}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pagination
          paginationMode='client'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          initialState={{
            columns: {
              columnVisibilityModel: { score: false, item_number: false },
            },
            density: 'compact',
          }}
          localeText={{
            noRowsLabel: isLoading ? '' : 'No Suggestions Found',
          }}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: theme.palette.grey[100],
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '1rem',
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
              py: 0.5,
            },
            '& .MuiDataGrid-cell[data-field="description"]': {
              py: 0.75,
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
              px: '4px !important',
            },
          }}
        />
      </Box>

      <RowDetailModal
        open={modalOpen}
        handleClose={handleItemCloseModal}
        itemNumber={selectedItemNumber}
      />
      <Modal open={isOrderModalOpen} onClose={closeOrderModal}>
        <OrderConfirmModal
          handleClose={closeOrderModal}
          handleConfirm={handleConfirmOrder}
          customerNumber={token}
          webOrderId={webOrderId}
        />
      </Modal>
      <AddItemPopup
        open={openAddItemModal}
        onClose={() => setOpenAddItemModal(false)}
        token={token}
        selectedItem={selectedItemForAdd}
      />
    </Box>
  );
};

export default SuggestionsView;
